<template>
  <q-dialog 
    v-model="dialogValue" 
    transition-show="fade" 
    transition-hide="fade"
    position="standard"
    class="modern-coupon-modal flex flex-center"
    :full-height="false"
  >
    <q-card class="modern-coupon-card">
      <!-- Header Section - Mobile & Desktop -->
      <q-card-section :class="['modern-coupon-header', $q.screen.lt.md ? 'q-pa-sm' : 'q-pa-md']">
        <div class="row items-center no-wrap full-width">
          <store-logo 
            v-if="coupon?.store?.logo_url" 
            :logo-url="coupon.store.logo_url" 
            :size="$q.screen.lt.md ? '36px' : '24px'" 
            custom-class="q-mr-sm align-self-center"
          />
          <div class="text-h6 ellipsis flex-grow-1 text-center">{{ coupon?.title }}</div>
          <q-btn icon="close" flat round dense v-close-popup @click="$emit('close')" class="q-ml-sm" />
        </div>
      </q-card-section>
      <q-separator />
      <!-- Main Content Section -->
      <q-card-section v-if="coupon" class="q-pa-none scroll">
        <!-- Desktop Layout -->
        <div v-if="!$q.screen.lt.md" class="column q-pa-sm">
          <div class="desktop-content-container">
            <!-- Top section: Coupon Image and Category - More Compact -->
            <div class="row q-col-gutter-sm q-mb-sm">
              <!-- Image Column -->
              <div class="col-3 flex items-start justify-center">
                <div class="coupon-image-box shadow-3 flex flex-center">
                  <coupon-image
                    :src="coupon.image_url || coupon.store?.logo_url"
                    :ratio="1"
                    custom-class="coupon-img"
                    error-icon-size="50px"
                    style="max-width: 80px; max-height: 80px;"
                  >
                    <div class="absolute-top-right q-ma-xs" v-if="coupon.verified">
                      <q-badge color="green" class="verified-badge q-pa-xs">
                        <q-icon name="verified" size="xs" class="q-mr-xs"></q-icon>
                        <span class="text-caption">Verified</span>
                      </q-badge>
                    </div>
                  </coupon-image>
                </div>
              </div>
                <!-- Category and Description - Shifted up -->
              <div class="col-9">
                <div class="row items-center justify-between no-wrap q-mb-xs">
                  <div class="description-text-container flex-grow-1">
                    <p class="text-body2 description-text q-my-none" v-if="coupon.description">{{ coupon.description }}</p>
                    <p class="text-body2 text-italic text-grey-7 q-my-none" v-else>No description available</p>
                  </div>
                  <q-chip size="sm" dense outline color="primary" class="q-ml-xs">{{ coupon.store?.category || 'Retailer' }}</q-chip>
                </div>
              </div>
            </div><!-- Middle section: Combined Coupon Code and Stats -->
            <div class="q-mb-md">              <q-card class="action-card q-pa-sm shadow-2 text-center">
                <!-- Coupon Code Section (when code exists) -->
                <template v-if="coupon.code">
                  <div class="text-subtitle2 text-weight-bold q-mb-xs">Coupon Code</div>
                  <div class="q-mb-sm">
                    <coupon-code
                      :code="coupon.code"
                      label="Coupon Code:"
                      class="q-mb-sm coupon-code-display"
                      @code-copied="copyCode(coupon.code)"
                    />
                  </div>
                    <!-- Coupon Action Buttons - More Compact -->
                  <div class="row q-col-gutter-sm q-mb-xs">
                    <div class="col-6">
                      <q-btn
                        unelevated
                        color="primary"
                        icon="content_copy"
                        :label="copying ? 'Copying' : 'Copy'"
                        class="full-width action-btn hover-lift"
                        @click="copyCode(coupon.code)"
                        :loading="copying"
                        :disable="copying"
                        size="sm"
                      />
                    </div>
                    <div class="col-6">
                      <q-btn
                        unelevated
                        color="deep-orange"
                        icon="launch"
                        label="Visit Store"
                        class="full-width action-btn hover-lift"
                        @click="visitStore(coupon)"
                        :loading="visiting"
                        :disable="visiting"
                        size="sm"
                      />
                    </div>
                  </div>
                </template>
                  <!-- Deal Section (when no code) - More Compact -->
                <template v-else>
                  <div class="text-subtitle2 text-weight-bold q-my-sm text-positive">OFFER ACTIVATED</div>
                  <q-btn
                    unelevated
                    color="deep-orange"
                    icon="launch"
                    label="Visit Store"
                    class="full-width action-btn hover-lift q-mb-sm"
                    @click="visitStore(coupon)"
                    :loading="visiting"
                    :disable="visiting"
                    size="sm"
                  />
                </template>                <!-- Statistics inside the same card - Ultra compact -->
                <q-separator class="q-my-xs" />
                
                <div class="stats-section compact">
                  <div class="text-caption text-weight-bold q-mb-xs q-mt-xs">Coupon Statistics</div>
                  <div class="row q-col-gutter-xs">
                    <div class="col-4">
                      <stat-card 
                        icon="thumb_up"
                        icon-color="positive" 
                        :value="`${coupon.success_rate || '95'}%`"
                        label="Success"
                        compact
                      />
                    </div>
                    <div class="col-4">
                      <stat-card 
                        icon="people"
                        icon-color="primary" 
                        :value="coupon.uses_count || '124'"
                        label="Uses"
                        compact
                      />
                    </div>
                    <div class="col-4">
                      <stat-card 
                        icon="update"
                        icon-color="teal" 
                        :value="coupon.last_tested ? formatDate(coupon.last_tested) : 'Today'"
                        label="Verified"
                        compact
                      />
                    </div>
                  </div>
                </div>
              </q-card>
            </div>
              <q-expansion-item
                v-if="coupon.terms"
                icon="gavel"
                label="Terms & Conditions"
                header-class="text-primary"
                expand-icon-class="text-primary"
                class="q-mb-sm terms-section"
                dense
                bordered
              >
                <q-card>
                  <q-card-section class="q-pa-sm">
                    <p class="text-caption">{{ coupon.terms }}</p>
                  </q-card-section>
                </q-card>
              </q-expansion-item>              <!-- Similar Offers Section - Very Compact -->
              <div class="q-mt-sm" v-if="similarCoupons && similarCoupons.length">
                <div class="text-caption text-weight-bold q-mb-xs">Similar Offers You Might Like</div>
                <div class="row q-col-gutter-xs">
                  <div 
                    v-for="similarCoupon in similarCoupons.slice(0, 2)" 
                    :key="similarCoupon.id" 
                    class="col-6"
                  >
                    <similar-coupon-item 
                      :coupon="similarCoupon"
                      :is-mobile="false"
                      compact
                      @view="$emit('view-similar', similarCoupon)"
                    />
                  </div>
                </div>
                <div class="text-center q-mt-sm">
                  <q-btn label="View All Offers" outline color="primary" size="sm" dense @click="$emit('close')" />
                </div>
              </div>
          </div><!-- End desktop-content-container -->
        </div>
          <!-- Mobile Layout -->
        <div v-else class="column">
          <!-- Mobile Banner - Simplified to match desktop -->
          <div class="bg-primary-1 mobile-banner flex items-center justify-between">
            <div class="text-body2 ellipsis flex-grow-1 text-center">{{ coupon?.title }}</div>
            <div class="verified-badge-container q-ml-sm" v-if="coupon.verified">
              <q-badge color="green" class="q-pa-xs verified-badge">
                <q-icon name="verified" size="xs" class="q-mr-xs"></q-icon>
                Verified
              </q-badge>
            </div>
          </div>
            <!-- Mobile Content -->
          <div class="q-pa-md mobile-content" :class="{'no-bottom-actions': !coupon.code}">
            <!-- Mobile Top Section: Image and Description (Similar to Desktop) -->
            <div class="row q-col-gutter-sm q-mb-sm">
              <!-- Image Column -->
              <div class="col-3 flex items-start justify-center">
                <div class="coupon-image-box shadow-3 flex flex-center">
                  <coupon-image
                    :src="coupon.image_url || coupon.store?.logo_url"
                    :ratio="1"
                    custom-class="mobile-coupon-img"
                    error-icon-size="50px"
                    style="max-width: 80px; max-height: 80px;"
                  >
                    <div class="absolute-top-right q-ma-xs" v-if="coupon.verified && !coupon.store?.logo_url">
                      <q-badge color="green" class="verified-badge q-pa-xs">
                        <q-icon name="verified" size="xs" class="q-mr-xs"></q-icon>
                        <span class="text-caption">Verified</span>
                      </q-badge>
                    </div>
                  </coupon-image>
                </div>
              </div>
              
              <!-- Category and Description - Similar to Desktop -->
              <div class="col-9">
                <div class="row items-center justify-between no-wrap q-mb-xs">
                  <div class="description-text-container flex-grow-1">
                    <p class="text-caption description-text q-my-none" v-if="coupon.description">{{ coupon.description }}</p>
                    <p class="text-caption text-italic text-grey-7 q-my-none" v-else>No description available</p>
                  </div>
                  <q-chip size="sm" dense outline color="primary" class="q-ml-xs">{{ coupon.store?.category || 'Retailer' }}</q-chip>
                </div>
              </div>
            </div>
            
            <!-- Mobile Coupon Code or Deal -->
            <div v-if="coupon.code" class="q-mb-sm text-center mobile-code-section">
              <div class="text-subtitle2 text-weight-bold q-mb-xs">Coupon Code</div>
              <coupon-code
                :code="coupon.code"
                :label="null"
                compact
                class="q-mb-sm"
                button-size="sm"
                @code-copied="copyCode(coupon.code)"
              />
              <div class="row q-col-gutter-xs">
                <div class="col-6">
                  <q-btn
                    unelevated
                    color="primary"
                    icon="content_copy"
                    :label="copying ? 'Copying...' : 'Copy'"
                    class="full-width hover-lift"
                    @click="copyCode(coupon.code)"
                    :loading="copying"
                    :disable="copying"
                    no-caps
                    dense
                    size="sm"
                  />
                </div>
                <div class="col-6">
                  <q-btn
                    unelevated
                    color="deep-orange"
                    icon="launch"
                    label="Visit"
                    class="full-width hover-lift"
                    @click="visitStore(coupon)"
                    :loading="visiting"
                    :disable="visiting"
                    no-caps
                    dense
                    size="sm"
                  />
                </div>
              </div>
            </div>
            <div v-else class="q-mb-md mobile-deal-section">
              <q-card class="q-pa-sm shadow-2 text-center">
                <div class="text-subtitle2 text-weight-bold q-mb-xs text-positive">OFFER ACTIVATED</div>
                <q-btn
                  unelevated
                  color="deep-orange"
                  icon="launch"
                  label="Visit Store"
                  class="full-width mobile-action-btn"
                  @click="visitStore(coupon)"
                  :loading="visiting"
                  :disable="visiting"
                  no-caps
                  size="md"
                />
              </q-card>
            </div>
            <!-- Mobile Stats -->
            <div class="q-mb-md">
              <q-card class="mobile-stat-card q-pa-sm">
                <div class="text-subtitle2 text-weight-bold q-mb-xs">Coupon Statistics</div>
                <div class="row q-col-gutter-xs">
                  <div class="col-4">
                    <stat-card 
                      icon="thumb_up"
                      icon-color="positive" 
                      :value="`${coupon.success_rate || '95'}%`"
                      label="Success"
                      is-mobile
                    />
                  </div>
                  <div class="col-4">
                    <stat-card 
                      icon="people"
                      icon-color="primary" 
                      :value="coupon.uses_count || '124'"
                      label="Uses"
                      is-mobile
                    />
                  </div>
                  <div class="col-4">
                    <stat-card 
                      icon="update"
                      icon-color="teal" 
                      :value="coupon.last_tested ? formatDate(coupon.last_tested) : 'Today'"
                      label="Verified"
                      is-mobile
                    />
                  </div>
                </div>
              </q-card>
            </div>
            
            <!-- Mobile Terms -->
            <q-expansion-item
              v-if="coupon.terms"
              icon="gavel"
              label="Terms & Conditions"
              header-class="text-primary"
              expand-icon-class="text-primary"
              dense
              class="q-mb-md"
              bordered
            >
              <q-card>
                <q-card-section>
                  <p class="text-caption text-weight-medium">{{ coupon.terms }}</p>
                </q-card-section>
              </q-card>
            </q-expansion-item>
            <!-- Mobile Similar Offers -->
            <div v-if="similarCoupons && similarCoupons.length">
              <div class="text-subtitle2 text-weight-bold q-mb-sm">Similar Offers</div>
              <div class="q-gutter-y-sm">
                <similar-coupon-item
                  v-for="similarCoupon in similarCoupons.slice(0, 2)"
                  :key="similarCoupon.id"
                  :coupon="similarCoupon"
                  :is-mobile="true"
                  @view="$emit('view-similar', similarCoupon)"
                />
              </div>
            </div>
            <!-- View All button -->
            <div class="text-center q-mt-lg q-mb-xl">
              <q-btn label="View All Coupons" outline color="primary" size="sm" @click="$emit('close')" />
            </div>
            <!-- Bottom spacer for fixed action bar -->
            <div class="fixed-bottom-spacer"></div>
          </div>
        </div>
      </q-card-section>
    </q-card>
  </q-dialog>
</template>

<script setup>
import { computed } from 'vue';
import { useQuasar } from 'quasar';
import { formatDate } from '@/utils/dateUtils';
import { useCouponActions } from '@/composables/useCoupon';
import CouponImage from './CouponImage.vue';
import StoreLogo from './StoreLogo.vue';
import CouponCode from './CouponCode.vue';
import StatCard from './StatCard.vue';
import SimilarCouponItem from './SimilarCouponItem.vue';
import './CouponDetailDialog.scss';

const props = defineProps({
  modelValue: Boolean,
  coupon: Object,
  similarCoupons: {
    type: Array,
    default: () => []
  }
});
const emit = defineEmits(['update:modelValue', 'close', 'view-similar']);
const $q = useQuasar();
const { copying, visiting, copyCode, visitStore } = useCouponActions();

// Create a local ref for dialog visibility
const dialogValue = computed({
  get() {
    return props.modelValue;
  },
  set(val) {
    emit('update:modelValue', val);
  }
});
</script>
